(() => {
  if (!document.body.classList.contains('template-home')) return;

  /* ── Scroll reveal (.r elements), re-scoped per init() call so Theme
     Editor section reloads pick up newly inserted elements without
     re-observing already-revealed ones. ── */
  const observer = ('IntersectionObserver' in window)
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('on');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.08 })
    : null;

  const revealIn = (root) => {
    const items = root.querySelectorAll('.r:not(.on)');
    if (observer) items.forEach((item) => observer.observe(item));
    else items.forEach((item) => item.classList.add('on'));
  };

  /* Only the video for the active viewport is allowed to load/play. The
     previous markup autoplayed both desktop and mobile campaign videos. */
  const initHeroPlayback = (root) => {
    const heroes = root.matches?.('.hero') ? [root] : [...root.querySelectorAll('.hero')];
    heroes.forEach((hero) => {
      if (hero.dataset.playbackBound) return;
      const desktop = hero.querySelector('.hero-video--desktop');
      const mobile = hero.querySelector('.hero-video--mobile');
      if (!desktop && !mobile) return;
      hero.dataset.playbackBound = 'true';
      /* Safari is stricter than Chrome about an immediate .play() call
         issued before enough data is buffered — with preload="metadata"
         only metadata loads eagerly, so the very first play() attempt (right
         as this script runs) can reject there even though the video element
         itself is fine. Chrome tends to queue/retry this internally; Safari
         doesn't, so a silently-swallowed rejection here reads as "the video
         never loads" even though it's really just a stalled autoplay
         attempt. Retry once the video reports it has enough data to play. */
      const attemptPlay = (video) => {
        if (!video) return;
        if (document.hidden) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('defaultmuted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('autoplay', '');
        if (video.preload !== 'auto') video.preload = 'auto';
        if (video.readyState < 2) {
          try { video.load(); } catch (error) {}
        }
        const play = () => video.play().catch(() => {});
        video.play().catch(() => {
          const retry = () => { video.play().catch(() => {}); };
          video.addEventListener('loadeddata', retry, { once: true });
          video.addEventListener('canplay', retry, { once: true });
          video.addEventListener('canplaythrough', retry, { once: true });
        });
        requestAnimationFrame(play);
        setTimeout(play, 250);
        setTimeout(play, 1000);
      };
      const mobileQuery = window.matchMedia('(max-width: 768px)');
      const sync = () => {
        const active = mobileQuery.matches ? mobile : desktop;
        const inactive = mobileQuery.matches ? desktop : mobile;
        inactive?.pause();
        attemptPlay(active);
      };
      mobileQuery.addEventListener?.('change', sync);
      window.addEventListener('pageshow', sync);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) sync();
      });
      sync();
    });
  };

  /* Reels play only while visible instead of running all 13 videos at once. */
  const reelObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.play().catch(() => {});
          else entry.target.pause();
        });
      }, { threshold: 0.2 })
    : null;

  const initReelPlayback = (root) => {
    root.querySelectorAll('.reel-tile video:not([data-playback-bound])').forEach((video) => {
      video.dataset.playbackBound = 'true';
      if (reelObserver) reelObserver.observe(video);
      else video.play().catch(() => {});
    });
  };

  /* ── data-quiz-link / data-bundle-link: click-through helpers.
     URL is read from the nearest ancestor carrying the matching
     data attribute (each section sets its own). ── */
  const bindLinkRedirect = (root, attr, dataKey) => {
    root.querySelectorAll(`[${attr}]:not([data-bound-${dataKey}])`).forEach((item) => {
      item.setAttribute(`data-bound-${dataKey}`, 'true');
      item.addEventListener('click', () => {
        const url = item.closest(`[data-${dataKey}-url]`)?.dataset[`${dataKey}Url`];
        if (url) window.location.href = url;
      });
    });
  };

  /* ── Watch & Shop: reel prev/next, lightbox, featured variant switch ── */
  const initWatchShop = (root) => {
    const reelTrack = root.querySelector('#reelTrack');
    const reelPrev = root.querySelector('#reelPrev');
    const reelNext = root.querySelector('#reelNext');
    if (reelPrev && !reelPrev.dataset.bound) {
      reelPrev.dataset.bound = 'true';
      reelPrev.addEventListener('click', () => reelTrack?.scrollBy({ left: -320, behavior: 'smooth' }));
    }
    if (reelNext && !reelNext.dataset.bound) {
      reelNext.dataset.bound = 'true';
      reelNext.addEventListener('click', () => reelTrack?.scrollBy({ left: 320, behavior: 'smooth' }));
    }

    const lightbox = root.querySelector('#reelLightbox');
    const lightboxVideo = root.querySelector('#reelLightboxVideo');
    const lightboxTitle = root.querySelector('#reelLightboxTitle');
    const closeLightbox = () => {
      if (!lightbox) return;
      lightbox.classList.remove('open');
      lightbox.setAttribute('aria-hidden', 'true');
      lightboxVideo?.pause();
      if (lightboxVideo) lightboxVideo.removeAttribute('src');
      document.body.classList.remove('is-locked');
    };
    window.openReelLightbox = (button) => {
      if (!lightbox || !lightboxVideo) return;
      lightboxVideo.src = button.dataset.reelSrc;
      if (lightboxTitle) lightboxTitle.textContent = button.dataset.reelTitle || '';
      lightbox.classList.add('open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      lightboxVideo.play().catch(() => {});
      lightbox.querySelector('#reelLightboxClose')?.focus();
    };
    const closeBtn = root.querySelector('#reelLightboxClose');
    if (closeBtn && !closeBtn.dataset.bound) {
      closeBtn.dataset.bound = 'true';
      closeBtn.addEventListener('click', closeLightbox);
    }
    if (lightbox && !lightbox.dataset.bound) {
      lightbox.dataset.bound = 'true';
      lightbox.addEventListener('click', (event) => { if (event.target === lightbox) closeLightbox(); });
    }
    if (!document.body.dataset.reelEscBound) {
      document.body.dataset.reelEscBound = 'true';
      document.addEventListener('keydown', (event) => {
        const openLightbox = document.querySelector('#reelLightbox.open');
        if (event.key === 'Escape' && openLightbox) {
          openLightbox.classList.remove('open');
          openLightbox.setAttribute('aria-hidden', 'true');
          openLightbox.querySelector('video')?.pause();
          document.body.classList.remove('is-locked');
        }
      });
    }

    const variantButtons = root.querySelectorAll('[data-wb-variant-id]');
    const variantInput = root.querySelector('[data-wb-variant-input]');
    const watchPrice = root.querySelector('#wbPrice');
    variantButtons.forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        variantButtons.forEach((item) => item.classList.toggle('active', item === button));
        if (variantInput) variantInput.value = button.dataset.wbVariantId;
        if (watchPrice) watchPrice.textContent = button.dataset.wbPrice;
      });
    });
  };

  /* ── BYOB teaser: quantity-stepper preview + live subtotal + hand-off ──
     Mirrors the real bundle-builder's interaction pattern, but this is a
     preview only — it never touches the cart. The summary button hands
     off to the real BYOB page (data-bundle-url) to complete the bundle. ── */
  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: window.themeCurrency || 'INR' }).format(cents / 100);
  };

  const initByob = (root) => {
    /* root is document on the initial page load (see init(document) below)
       but the specific .byob section element on later shopify:section:load
       reloads — only the section itself carries the data-byob-max-items /
       data-tier-*-percent attributes read below, so resolve it explicitly
       rather than assuming root has a .dataset (document doesn't, which
       threw here and silently broke every click handler on first load). */
    const section = root.querySelector('.byob') || (root.matches?.('.byob') ? root : null);
    if (!section) return;
    const cards = [...section.querySelectorAll('[data-byob-card]')];
    if (!cards.length) return;
    const thumbsEl = section.querySelector('[data-byob-thumbs]');
    const emptyText = section.querySelector('[data-byob-empty-text]');
    const subtotalEl = section.querySelector('[data-byob-subtotal]');
    const actualEl = section.querySelector('[data-byob-actual]');
    const savingEl = section.querySelector('[data-byob-saving]');
    const bundleButton = section.querySelector('#byobAddAll');
    if (!thumbsEl || !subtotalEl || !bundleButton || bundleButton.dataset.byobBound) return;
    bundleButton.dataset.byobBound = 'true';

    const items = new Map();
    const maxItems = Math.max(1, Number(section.dataset.byobMaxItems || 6));
    const tierAttr = (min) => Number(section.getAttribute(`data-tier-${min}-percent`) || 0);
    const tiers = [
      { min: 6, percent: tierAttr(6) },
      { min: 5, percent: tierAttr(5) },
      { min: 4, percent: tierAttr(4) },
      { min: 3, percent: tierAttr(3) },
      { min: 2, percent: tierAttr(2) }
    ];
    const getTotalQuantity = () => [...items.values()].reduce((sum, item) => sum + item.quantity, 0);
    const getTier = (count) => tiers.find((tier) => count >= tier.min && tier.percent > 0) || null;

    const render = () => {
      const values = [...items.values()];
      const totalQuantity = getTotalQuantity();
      thumbsEl.querySelectorAll('.byob-summary-thumb').forEach((el) => el.remove());
      if (emptyText) emptyText.hidden = values.length > 0;
      values.forEach((item) => {
        const thumb = document.createElement('span');
        thumb.className = 'byob-summary-thumb';
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.name;
        image.loading = 'lazy';
        thumb.appendChild(image);
        if (item.quantity > 1) {
          const quantity = document.createElement('span');
          quantity.className = 'byob-summary-thumb-qty';
          quantity.textContent = item.quantity;
          thumb.appendChild(quantity);
        }
        thumbsEl.appendChild(thumb);
      });
      const subtotal = values.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const tier = getTier(totalQuantity);
      const discount = tier ? Math.round(subtotal * tier.percent / 100) : 0;
      if (actualEl) actualEl.textContent = formatMoney(subtotal);
      subtotalEl.textContent = formatMoney(Math.max(0, subtotal - discount));
      if (savingEl) {
        savingEl.hidden = discount <= 0;
        savingEl.textContent = tier ? `${tier.percent}% off applied. You save ${formatMoney(discount)}` : '';
      }
      bundleButton.disabled = totalQuantity === 0;
      bundleButton.style.opacity = totalQuantity === 0 ? '0.4' : '1';
      cards.forEach((card) => {
        const stateQuantity = Number(card.querySelector('[data-byob-qty-value]')?.textContent || 0);
        const add = card.querySelector('[data-byob-add-item]');
        if (add) add.disabled = !card.dataset.byobVariantId || (totalQuantity >= maxItems && stateQuantity === 0);
      });
    };

    cards.forEach((card) => {
      if (card.dataset.bound) return;
      card.dataset.bound = 'true';
      const variantId = card.dataset.byobVariantId;
      if (!variantId) return;
      const price = Number(card.dataset.byobPrice || 0);
      const name = card.dataset.byobProductName || '';
      const image = card.querySelector('img')?.src;
      const qtyValue = card.querySelector('[data-byob-qty-value]');
      const addBtn = card.querySelector('[data-byob-add-item]');
      let quantity = 0;

      const setQuantity = (next) => {
        const currentTotal = getTotalQuantity();
        const requested = Math.max(0, next);
        const allowed = Math.max(0, maxItems - (currentTotal - quantity));
        quantity = Math.min(requested, allowed);
        qtyValue.textContent = quantity;
        if (quantity > 0) items.set(variantId, { quantity, price, name, image });
        else items.delete(variantId);
        render();
      };

      addBtn?.addEventListener('click', () => setQuantity(quantity + 1));
      card.querySelectorAll('[data-byob-qty]').forEach((btn) => {
        btn.addEventListener('click', () => setQuantity(quantity + Number(btn.dataset.byobQty)));
      });
    });

    bundleButton.addEventListener('click', () => {
      const url = bundleButton.closest('[data-bundle-url]')?.dataset.bundleUrl;
      /* This preview never touches the cart (see the module comment above),
         so the only way the BYOB page can pick up what was started here is
         a short-lived handoff via sessionStorage — read once and cleared by
         bundle-builder.js on the next page load. */
      if (items.size) {
        try {
          sessionStorage.setItem('chantero-bundle-transfer', JSON.stringify([...items.entries()].map(([variantId, item]) => ({ variantId, quantity: item.quantity }))));
        } catch (error) {
          /* sessionStorage unavailable (private browsing, etc.) — the bundle just won't carry over */
        }
      }
      if (url) window.location.href = url;
    });

    render();
  };

  const init = (root) => {
    revealIn(root);
    initHeroPlayback(root);
    initReelPlayback(root);
    bindLinkRedirect(root, 'data-quiz-link', 'quiz');
    bindLinkRedirect(root, 'data-bundle-link', 'bundle');
    initWatchShop(root);
    initByob(root);
  };

  init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
